import type { ProductCatalogItem } from '@/services/products';
import { getProductGroup } from './product-groups.util';

/** Ações explícitas — não é sincronização: produto fora da lista fica intocado. */
export type BulkProductAction = 'ACTIVATE' | 'DEACTIVATE';

/**
 * Teto de corretoras por chamada. Espelha `BULK_MAX_BROKERS` da Api
 * (`Api/src/parties/brokers/brokers.constants.ts`): passar disso devolve 400.
 */
export const BULK_CHUNK_SIZE = 20;

/** Teto de produtos por chamada — espelha `BULK_MAX_PRODUCTS` da Api. */
export const BULK_MAX_PRODUCTS = 10;

/**
 * Teto da seleção inteira.
 *
 * Não é limite da Api (o front fatia em lotes sequenciais de BULK_CHUNK_SIZE):
 * é o teto do índice de parties do InsureMO, que devolve 500 quando
 * PageNo x PageSize passa de 1000. Como também é o `pageSize` do "selecionar
 * todas do filtro", subir além de 1000 quebra esse botão — e paginar não fura
 * o teto. 1000 corretoras são 50 lotes: alguns minutos olhando a barra, e até
 * ~20 min se o InsureMO estiver lento (cada lote corta em ~22s na Api).
 */
export const BULK_MAX_SELECTION = 1000;

/**
 * Produtos fora da edição em massa.
 *
 * Pacote Telefônica (140005 Residencial, 180008 Empresarial): o vínculo é
 * comercial, corretora a corretora — liberar em lote colocaria o pacote em quem
 * não tem contrato.
 * Espelha `BULK_BLOCKED_PRODUCT_CODES` da Api, que é o gate de verdade.
 */
export const BULK_BLOCKED_PRODUCT_CODES = ['140005', '180008'];

/**
 * Produto elegível à operação em massa.
 *
 * Fora os bloqueados por código, exclui os de teste — prefixo `TEST` no código
 * ou `(Test)` no nome, que é como o InsureMO os nomeia hoje.
 */
export function isBulkEditableProduct(product: {
  code: string;
  name?: string;
}): boolean {
  const code = (product.code ?? '').toUpperCase();

  if (BULK_BLOCKED_PRODUCT_CODES.includes(code)) return false;
  if (code.startsWith('TEST')) return false;
  if (/\(test\)/i.test(product.name ?? '')) return false;

  return true;
}

/** Só o que a lista de seleção e a tela de resultado precisam guardar. */
export interface BulkBrokerRef {
  code: string;
  name: string;
  document?: string;
  location?: string;
}

/** Uma corretora e os produtos que ela deve receber nesta rodada. */
export interface BulkProductJob {
  broker: BulkBrokerRef;
  productCodes: string[];
}

export interface BulkBrokerFailure {
  brokerCode: string;
  brokerName: string;
  productCodes: string[];
  message: string;
}

export interface BulkRunTotals {
  updated: number;
  skipped: number;
  failed: number;
  brokersOk: number;
}

export interface ProductGroupOption {
  key: string;
  label: string;
  products: ProductCatalogItem[];
}

/** Estado de um checkbox de grupo na árvore de produtos. */
export type GroupCheckboxState = boolean | 'indeterminate';

/**
 * Agrupa o catálogo nos mesmos rótulos da tela individual (Patrimonial,
 * Vida/Pessoas), reusando o de-para de categorias.
 */
export function groupCatalogProducts(
  products: ProductCatalogItem[]
): ProductGroupOption[] {
  const groups = products.filter(isBulkEditableProduct).reduce(
    (acc, product) => {
      const { key, label } = getProductGroup(product.category);
      acc[key] ||= { key, label, products: [] };
      acc[key].products.push(product);
      return acc;
    },
    {} as Record<string, ProductGroupOption>
  );

  return Object.values(groups)
    .map(group => ({
      ...group,
      products: [...group.products].sort((a, b) =>
        a.name.localeCompare(b.name, 'pt-BR')
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

export function resolveGroupCheckboxState(
  group: ProductGroupOption,
  selected: ReadonlySet<string>
): GroupCheckboxState {
  if (group.products.length === 0) return false;

  const checked = group.products.filter(p => selected.has(p.code)).length;

  if (checked === 0) return false;
  if (checked === group.products.length) return true;
  return 'indeterminate';
}

/**
 * Alterna um grupo inteiro. Grupo parcialmente marcado vira totalmente marcado
 * — mesmo comportamento da modal individual.
 */
export function toggleGroupSelection(
  group: ProductGroupOption,
  selected: ReadonlySet<string>
): Set<string> {
  const next = new Set(selected);
  const isFullyChecked = resolveGroupCheckboxState(group, selected) === true;

  group.products.forEach(product => {
    if (isFullyChecked) {
      next.delete(product.code);
    } else {
      next.add(product.code);
    }
  });

  return next;
}

/** Quebra os jobs em lotes do tamanho que a Api aceita por chamada. */
export function chunkJobs(
  jobs: BulkProductJob[],
  size: number = BULK_CHUNK_SIZE
): BulkProductJob[][] {
  const safeSize = Math.max(1, size);
  const chunks: BulkProductJob[][] = [];

  for (let i = 0; i < jobs.length; i += safeSize) {
    chunks.push(jobs.slice(i, i + safeSize));
  }

  return chunks;
}

/**
 * Agrupa jobs que compartilham exatamente os mesmos produtos.
 *
 * A primeira rodada é sempre um grupo só (todas as corretoras recebem a mesma
 * lista) e vira uma chamada por lote. Na retentativa cada corretora pode ter
 * falhado num produto diferente, e aí sai uma chamada por combinação — o
 * endpoint aplica os MESMOS produtos a todas as corretoras do payload.
 */
export function groupJobsByProducts(
  jobs: BulkProductJob[]
): { productCodes: string[]; brokers: BulkBrokerRef[] }[] {
  const byKey = new Map<
    string,
    { productCodes: string[]; brokers: BulkBrokerRef[] }
  >();

  jobs.forEach(job => {
    if (job.productCodes.length === 0) return;

    const codes = [...job.productCodes].sort();
    const key = codes.join('|');
    const entry = byKey.get(key) ?? { productCodes: codes, brokers: [] };
    entry.brokers.push(job.broker);
    byKey.set(key, entry);
  });

  return [...byKey.values()];
}

/**
 * Monta os jobs da retentativa: só as corretoras que falharam, e em cada uma só
 * os produtos que falharam nela. Sem isso a retentativa reprocessaria todas as
 * corretoras que já deram certo.
 */
export function buildRetryJobs(
  failures: BulkBrokerFailure[]
): BulkProductJob[] {
  const byBroker = new Map<string, BulkProductJob>();

  failures.forEach(failure => {
    const existing = byBroker.get(failure.brokerCode);

    if (!existing) {
      byBroker.set(failure.brokerCode, {
        broker: { code: failure.brokerCode, name: failure.brokerName },
        productCodes: [...failure.productCodes],
      });
      return;
    }

    failure.productCodes.forEach(code => {
      if (!existing.productCodes.includes(code)) {
        existing.productCodes.push(code);
      }
    });
  });

  return [...byBroker.values()];
}

/**
 * Estimativa grosseira para o rodapé, em segundos.
 *
 * Baseada no custo medido no projeto (~0,35s por requisição ao InsureMO) e na
 * concorrência de 10 do backend: por corretora, 1 leitura mais uma escrita por
 * produto.
 */
export function estimateRunSeconds(
  brokerCount: number,
  productCount: number
): number {
  if (brokerCount === 0 || productCount === 0) return 0;

  const requestsPerBroker = 1 + productCount;
  const waves = Math.ceil(brokerCount / 10);

  return Math.ceil(waves * requestsPerBroker * 0.35);
}

/** "menos de 1 min" / "~3 min" — o rodapé não precisa de precisão. */
export function formatEstimate(seconds: number): string {
  if (seconds <= 0) return '';
  if (seconds < 60) return 'menos de 1 min';

  return `~${Math.ceil(seconds / 60)} min`;
}
